import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ColumnsIcon,
  GraphIcon,
  OpenInNewIcon,
  useDocumentTitle,
} from '@forge/common';
import { config } from '@onering/npm-package/web';
import { useServiceHealth } from '@/hooks/useServiceHealth';
import type { ServiceStatus } from '@/api/observability';

const statusMeta: Record<
  ServiceStatus,
  { label: string; core: string; badge: 'success' | 'warning' | 'error' | 'neutral' }
> = {
  operational: { label: 'Operational', core: 'bg-green-500', badge: 'success' },
  degraded: { label: 'Degraded', core: 'bg-yellow-500', badge: 'warning' },
  down: { label: 'Down', core: 'bg-red-500', badge: 'error' },
  unknown: { label: 'Unknown', core: 'bg-gray-400', badge: 'neutral' },
};

/**
 * Launchpad targets. URLs are resolved at runtime via `config()` so each
 * environment can point at its own host; the default is a host-relative path so
 * the link always resolves against whichever portal origin is serving the SPA.
 * `infraId` matches the corresponding entry in the Observability snapshot's
 * `infrastructure[]` so we can surface a live status pill.
 */
interface DatabaseTarget {
  id: string;
  name: string;
  description: string;
  infraId: string;
  urlConfigKey: string;
  fallbackPath: string;
}

const databases: DatabaseTarget[] = [
  {
    id: 'postgres',
    name: 'PostgreSQL Database',
    description:
      'OneRing platform application database. Browse schemas, run queries, and manage data through the web console.',
    infraId: 'postgres',
    urlConfigKey: 'POSTGRES_DB_URL',
    fallbackPath: '/postgres-database-console',
  },
  {
    id: 'oracle',
    name: 'Oracle Database',
    description:
      'Enterprise Oracle datastore. Open the web console to inspect tables, run SQL, and review data.',
    infraId: 'oracle',
    urlConfigKey: 'ORACLE_DB_URL',
    fallbackPath: '/database-console',
  },
  {
    id: 'mssql',
    name: 'Azure SQL Database',
    description:
      'Managed Azure SQL datastore for OneRing. Open the web console to inspect tables, run SQL, and review data.',
    infraId: 'mssql',
    urlConfigKey: 'AZURE_SQL_DB_URL',
    fallbackPath: '/database-console',
  },
];

const resolveUrl = (target: DatabaseTarget): string =>
  config(target.urlConfigKey) ?? target.fallbackPath;

const DatabaseCard = ({
  target,
  status,
  onViewMetrics,
}: {
  target: DatabaseTarget;
  status: ServiceStatus;
  onViewMetrics: () => void;
}) => {
  const meta = statusMeta[status];
  const href = resolveUrl(target);

  return (
    <Card className="h-full">
      <CardContent className="flex h-full flex-col gap-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-color-secondary dark:bg-gray-800 dark:text-color-secondary-dark">
              <ColumnsIcon />
            </span>
            <div className="min-w-0">
              <p className="truncate font-semibold">{target.name}</p>
              <p className="mt-0.5 text-xs text-color-tertiary">Database</p>
            </div>
          </div>
          <span className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full ${meta.core}`} aria-hidden="true" />
            <Badge type={meta.badge}>{meta.label}</Badge>
          </span>
        </div>

        <p className="text-sm text-color-tertiary">{target.description}</p>

        <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-200 pt-3 dark:border-gray-800">
          <Button
            variant="quiet-primary"
            icon={<GraphIcon />}
            onClick={onViewMetrics}
          >
            View metrics
          </Button>
          <Button
            as="link"
            href={href}
            variant="primary"
            icon={<OpenInNewIcon />}
            target="_blank"
            rel="noopener noreferrer"
            external
          >
            Open console
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const DatabasesPage = () => {
  useDocumentTitle('Databases · OneRing');
  const navigate = useNavigate();
  const { snapshot } = useServiceHealth();

  const statusFor = (infraId: string): ServiceStatus =>
    snapshot?.infrastructure.find((i) => i.id === infraId)?.status ?? 'unknown';

  return (
    <div className="space-y-6 p-4">
      <div className="min-w-0">
        <h2 className="text-xl font-bold">Databases</h2>
        <p className="mt-1 max-w-2xl text-sm text-color-tertiary">
          Quick access to the OneRing managed databases. Open a web console to browse and query, or
          jump to the Observability page for live health and metrics.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {databases.map((target) => (
          <DatabaseCard
            key={target.id}
            target={target}
            status={statusFor(target.infraId)}
            onViewMetrics={() => navigate('/observability')}
          />
        ))}
      </div>
    </div>
  );
};

export default DatabasesPage;
